import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useCalendarData } from './CalendarDataContext';

/**
 * A month at a time, plotted on a grid.
 *
 * The list on the home page answers "what is next". This answers "what does
 * March look like", which is the question somebody planning travel or capital
 * actually has, and a list cannot answer it at a glance.
 *
 * Only dated sales can be plotted. A sale known only as "typically September"
 * has no square to sit in, so those are listed under the grid rather than
 * dropped, which would make an empty month look like a month with no sales.
 */

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

function CalendarMonth() {
  const { api, path } = useCalendarData();
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [byDay, setByDay] = useState({});
  const [undated, setUndated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const data = await api(`/month?year=${year}&month=${month}`);
        if (cancelled) return;
        setByDay(data.byDay || {});
        setUndated(data.undated || []);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [year, month, api]);

  const step = (delta) => {
    const next = new Date(year, month - 1 + delta, 1);
    setYear(next.getFullYear());
    setMonth(next.getMonth() + 1);
  };

  const firstWeekday = new Date(year, month - 1, 1).getDay();
  const daysInMonth = new Date(year, month, 0).getDate();

  const cells = [];
  for (let i = 0; i < firstWeekday; i += 1) cells.push(null);
  for (let day = 1; day <= daysInMonth; day += 1) cells.push(day);

  return (
    <div>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        marginBottom: '14px',
        flexWrap: 'wrap'
      }}>
        <button className="btn btn-secondary" onClick={() => step(-1)}>Previous</button>
        <h1 style={{
          color: 'var(--color-heading)',
          fontSize: '1.2rem',
          margin: 0,
          flex: '1 1 auto',
          textAlign: 'center'
        }}>
          {MONTH_NAMES[month - 1]} {year}
        </h1>
        <button className="btn btn-secondary" onClick={() => step(1)}>Next</button>
      </div>

      {error && <p style={{ color: 'var(--color-danger-text, var(--color-text))' }}>{error}</p>}
      {loading && <p style={{ color: 'var(--color-text-muted)' }}>Loading</p>}

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        gap: '3px'
      }}>
        {WEEKDAYS.map(name => (
          <div
            key={name}
            style={{
              textAlign: 'center',
              fontSize: '0.72rem',
              color: 'var(--color-text-muted)',
              padding: '4px 0',
              fontWeight: 700
            }}
          >
            {name}
          </div>
        ))}

        {cells.map((day, index) => {
          const sales = day ? (byDay[String(day)] || []) : [];
          return (
            <div
              key={`${day || 'pad'}-${index}`}
              style={{
                minHeight: '68px',
                border: '1px solid var(--color-border)',
                borderRadius: '4px',
                padding: '3px',
                background: day ? 'var(--color-surface)' : 'transparent',
                overflow: 'hidden'
              }}
            >
              {day && (
                <div style={{ fontSize: '0.72rem', color: 'var(--color-text-muted)' }}>{day}</div>
              )}
              {sales.map(sale => (
                <Link
                  key={sale.id}
                  to={path(`/counties/${sale.countyId}`)}
                  title={`${sale.countyLabel}, ${sale.stateCode}: ${sale.saleKindLabel}`}
                  style={{
                    display: 'block',
                    fontSize: '0.68rem',
                    lineHeight: 1.25,
                    background: 'var(--color-primary)',
                    color: 'var(--color-primary-contrast, #fff)',
                    borderRadius: '3px',
                    padding: '2px 3px',
                    marginTop: '2px',
                    textDecoration: 'none',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {sale.countyLabel}, {sale.stateCode}
                </Link>
              ))}
            </div>
          );
        })}
      </div>

      {undated.length > 0 && (
        <section style={{ marginTop: '24px' }}>
          <h2 style={{ fontSize: '0.95rem', color: 'var(--color-heading)' }}>
            Expected this month, date not announced
          </h2>
          {undated.map(sale => (
            <div key={sale.id} style={{ padding: '8px 0', borderBottom: '1px solid var(--color-border)' }}>
              <Link
                to={path(`/counties/${sale.countyId}`)}
                style={{ color: 'var(--color-heading)', fontWeight: 600, textDecoration: 'none' }}
              >
                {sale.countyLabel}, {sale.stateCode}
              </Link>
              <div style={{ color: 'var(--color-text-muted)', fontSize: '0.85rem' }}>
                {[sale.saleKindLabel, sale.platformLabel].filter(Boolean).join(', ')}
              </div>
            </div>
          ))}
        </section>
      )}
    </div>
  );
}

export default CalendarMonth;
